import React from "react";
import { ReadOnlyNotice } from "./ReadOnlyNotice";

interface ReadOnlySurfaceProps {
	"data-testid"?: string;
	children: React.ReactNode;
	/** Override the banner copy (e.g. name the specific surface). */
	message?: string;
	/** Hide the banner when the caller renders its own read-only messaging. */
	showNotice?: boolean;
}

/** Keys that ACTIVATE a control. */
const ACTIVATION_KEYS = new Set(["Enter", " ", "Spacebar"]);

/**
 * Refuse activation and text entry, but never navigation: Tab, the arrows,
 * Escape, Home/End and the function keys all report a multi-character `key`,
 * so the surface stays fully navigable and readable by keyboard while a single
 * printable character — the one thing that would edit a contenteditable
 * rich-text field, which `disabled` cannot reach — is refused.
 */
const isMutatingKey = (key: string): boolean =>
	ACTIVATION_KEYS.has(key) || key.length === 1;

/**
 * Links out of the surface are navigation, not mutation — a read-only user must
 * still be able to follow them, so they are exempt from the click block. Native
 * form controls inside a disabled <fieldset> are already dead, so nothing here
 * needs to special-case them.
 */
const isNavigationTarget = (target: EventTarget | null): boolean =>
	target instanceof Element && target.closest("a[href]") !== null;

const block = (event: React.SyntheticEvent): void => {
	event.preventDefault();
	event.stopPropagation();
};

/**
 * PRO-709 — renders a surface for a user whose module capability is `view` but
 * not `edit`: everything is visible, nothing can be changed.
 *
 * ## Why a disabled <fieldset> and not a CSS class
 *
 * The acceptance criterion is that save/create/delete affordances are genuinely
 * unusable, "not merely visually muted". `fieldset[disabled]` is the platform's
 * own answer: every descendant <input>, <select>, <textarea> and <button> — the
 * newui <Button> included, which renders a native button — becomes disabled,
 * unclickable AND removed from the tab order, without each of the six RFx
 * settings tabs having to thread a `readOnly` prop through its whole component
 * tree. One wrapper cannot be forgotten by a future tab.
 *
 * The capture-phase handlers cover what `disabled` cannot reach: click targets
 * that are not form controls (a <div role="button">, a drag handle, a
 * react-select control) and text entry into contenteditable rich-text editors.
 * They run in the capture phase so the event is stopped BEFORE any descendant
 * handler sees it.
 *
 * This is a UX affordance, never access control — the API still refuses the
 * write. It exists so a view-only user is not invited to attempt one.
 */
export const ReadOnlySurface = ({
	children,
	message,
	showNotice = true,
	"data-testid": testId = "read-only-surface",
}: ReadOnlySurfaceProps) => (
	<div data-testid={testId}>
		{showNotice && <ReadOnlyNotice message={message} />}
		<fieldset
			aria-readonly="true"
			className="m-0 min-w-0 border-0 p-0"
			data-testid={`${testId}-fieldset`}
			disabled
			onBeforeInputCapture={block}
			onCutCapture={block}
			onClickCapture={(event) => {
				if (isNavigationTarget(event.target)) {
					return;
				}
				block(event);
			}}
			onDropCapture={block}
			onKeyDownCapture={(event) => {
				if (!isMutatingKey(event.key) || isNavigationTarget(event.target)) {
					return;
				}
				block(event);
			}}
			onPasteCapture={block}
		>
			{children}
		</fieldset>
	</div>
);
